import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { IoDownloadOutline, IoLanguageOutline, IoMenuOutline, IoCloseOutline } from "react-icons/io5";
import { SlWallet, SlGraph } from "react-icons/sl";
import { MdDarkMode, MdOutlineLightMode, MdOutlineDarkMode } from "react-icons/md";
import { HiChevronDown, HiOutlineDocumentText } from "react-icons/hi";
import { GoDownload } from "react-icons/go";
import { IoShieldOutline, IoKeyOutline, IoLogOutOutline, IoEyeOutline, IoEyeOffOutline, IoArrowForwardOutline, IoShareSocialOutline, IoDocumentTextOutline, IoTrendingUpOutline, IoNewspaperOutline, IoPieChartOutline, IoChevronDownOutline, IoChevronUpOutline, IoBarChartOutline, IoGiftOutline, IoAnalyticsOutline, IoMegaphoneOutline, IoHomeOutline } from "react-icons/io5";
import { PiUserCircleThin, PiSunDuotone, PiTranslateLight } from "react-icons/pi";
import { useContext } from 'react';
import { ThemeContext } from '../providers/ThemeProvider';
import useLanguage from '../hooks/useLanguage';

export default function Header() {
    const { t } = useTranslation();
    const { isAuthenticated, user, login, logout } = useAuth();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isBalanceVisible, setIsBalanceVisible] = useState(true);
    const [isTransactionHistoryOpen, setIsTransactionHistoryOpen] = useState(false);
    const [isPositionsOpen, setIsPositionsOpen] = useState(false);
    const [isAboutUsOpen, setIsAboutUsOpen] = useState(false);
    const [isAcademyOpen, setIsAcademyOpen] = useState(false);
    const [userInfo, setUserInfo] = useState(null);
    const { theme, toggleTheme } = useContext(ThemeContext);
    const [isVerified, setIsVerified] = useState(false);
    const [appLinks, setAppLinks] = useState({ ios: '', android: '' });
    const { lang, setLanguage } = useLanguage();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();
    const isSubpage = location.pathname !== '/';

    // Accordion menu items
    const transactionHistorySubItems = [
        { id: 'kripto-cekim', label: 'Kripto çekim işlemleri', path: '/crypto-withdrawals' },
        { id: 'banka-cekim', label: 'Banka çekim işlemleri', path: '/bank-withdrawals' }
    ];

    const positionsSubItems = [
        { id: 'acik-pozisyonlar', label: 'Açık pozisyonlar', path: '/open-positions' },
        { id: 'kapali-pozisyonlar', label: 'Kapalı pozisyonlar', path: '/closed-positions' }
    ];

    const aboutUsSubItems = [
        { id: 'introductiontext', path: '/introduction-text' },
        { id: 'vision', path: '/vision' },
        { id: 'mission', path: '/mission' },
        { id: 'activities', path: '/activities' },
        { id: 'management', path: '/management' },
        { id: 'news', path: '/news' }
    ];

    const academySubItems = [
        { id: 'blockchainNedir', path: '/blockchain-nedir' },
        { id: 'kripto-para-nedir', path: '/kripto-para-nedir' },
        { id: 'kyc-nedir', path: '/kyc-nedir' },
        { id: 'kyc-nasil-yapilir', path: '/kyc-nasil-yapilir' },
        { id: 'vadeli-islem-nedir', path: '/vadeli-islem-nedir' },
        { id: 'iki-faktorlu-dogrulama-nedir', path: '/iki-faktorlu-dogrulama-nedir' },
        { id: 'iki-faktorlu-dogrulama-aktif', path: '/iki-faktorlu-dogrulama-aktif' },
        { id: 'kripto-riskleri', path: '/kripto-riskleri' },
        { id: 'kripto-terimler', path: '/kripto-terimler' },
        { id: 'sik-sorulan-sorular', path: '/sik-sorulan-sorular' }
    ];

    useEffect(() => {
        const onScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Get user info when authenticated
    useEffect(() => {
        const fetchUserInfo = async () => {
            if (isAuthenticated) {
                try {
                    const response = await api.getUserInfo();
                    const userData = response.data?.data || response.data;
                    // Map camelCase to PascalCase for consistency
                    const normalizedData = {
                        ...userData,
                        Balance: userData.Balance ?? userData.balance,
                        Credit: userData.Credit ?? userData.credit,
                        FullName: userData.FullName ?? userData.fullName
                    };
                    setUserInfo(normalizedData);
                } catch (error) {
                    console.error('Error fetching user info:', error);
                }
            }
        };

        fetchUserInfo();
    }, [isAuthenticated]);

    // Fetch document verification status
    useEffect(() => {
        const fetchVerification = async () => {
            if (!isAuthenticated) {
                setIsVerified(false);
                return;
            }
            try {
                const res = await api.getExistingDocuments();
                const docs = res?.data || [];
                setIsVerified(Array.isArray(docs) && docs.length === 3);
            } catch (e) {
                // If call fails, keep previous state or mark as not verified
                setIsVerified(false);
            }
        };
        fetchVerification();
    }, [isAuthenticated]);

    // Fetch app links from company info
    useEffect(() => {
        const fetchApps = async () => {
            try {
                const res = await api.getCompanyInfo();
                const ios = res?.data?.data?.iosAppLink || '';
                const android = res?.data?.data?.androidAppLink || '';
                setAppLinks({ ios, android });
            } catch (e) {
                setAppLinks({ ios: '', android: '' });
            }
        };
        fetchApps();
    }, []);

    return (
        <>
            <header className={`header-container${isScrolled ? ' scrolled' : ''}${isSubpage ? ' subpage' : ''}`}>
                <div className="container-fluid">
                    <div className="d-flex align-items-center justify-content-between">
                        {/* Desktop Layout */}
                        <div className='d-none d-lg-flex align-items-center w-100'>
                            {/* Logo */}
                            <div className='logo-header'>
                                <Link to="/">
                                    <img src="/assets/images/nordex-logo.png" alt="nordex-logo" width="230px" />
                                </Link>
                            </div>

                            {/* Desktop Navigation */}
                            <ul className='list-unstyled mb-0 d-flex align-items-center left-navigation-list'>
                                <li>
                                    <Link to={isAuthenticated ? "/market" : "/login"}>{t('header.buyCrypto')}</Link>
                                </li>
                                <li>
                                    <Link to="/investment-options">{t('header.investmentOptions')}</Link>
                                </li>
                                <li className='header-dropdown'>
                                    <button className='header-dropdown-trigger align-items-center gap-1'>
                                        <span>{t('header.nordexAcademy')}</span>
                                        <HiChevronDown size={16} style={{marginLeft: 5}}/>
                                    </button>
                                    <div className='header-about-dropdown-menu'>
                                        <Link to="/blockchain-nedir" className='header-about-dropdown-item'>
                                            {t('header.academySubmenu.blockchainNedir')}
                                        </Link>
                                        <Link to="/kripto-para-nedir" className='header-about-dropdown-item'>
                                            {t('header.academySubmenu.kripto-para-nedir')}
                                        </Link>
                                        <Link to="/kyc-nedir" className='header-about-dropdown-item'>
                                            {t('header.academySubmenu.kyc-nedir')}
                                        </Link>
                                        <Link to="/kyc-nasil-yapilir" className='header-about-dropdown-item'>
                                            {t('header.academySubmenu.kyc-nasil-yapilir')}
                                        </Link>
                                        <Link to="/vadeli-islem-nedir" className='header-about-dropdown-item'>
                                            {t('header.academySubmenu.vadeli-islem-nedir')}
                                        </Link>
                                        <Link to="/iki-faktorlu-dogrulama-nedir" className='header-about-dropdown-item'>
                                            {t('header.academySubmenu.iki-faktorlu-dogrulama-nedir')}
                                        </Link>
                                        <Link to="/iki-faktorlu-dogrulama-aktif" className='header-about-dropdown-item'>
                                            {t('header.academySubmenu.iki-faktorlu-dogrulama-aktif')}
                                        </Link>
                                        <Link to="/kripto-riskleri" className='header-about-dropdown-item'>
                                            {t('header.academySubmenu.kripto-riskleri')}
                                        </Link>
                                        <Link to="/kripto-terimler" className='header-about-dropdown-item'>
                                            {t('header.academySubmenu.kripto-terimler')}
                                        </Link>
                                        <Link to="/sik-sorulan-sorular" className='header-about-dropdown-item'>
                                            {t('header.academySubmenu.sik-sorulan-sorular')}
                                        </Link>
                                    </div>
                                </li>
                                <li>
                                    <Link to="/promotions">{t('header.promotions')}</Link>
                                </li>
                                <li className='header-dropdown'>
                                    <button className='header-dropdown-trigger align-items-center gap-1'>
                                        <a>{t('header.aboutUs')}</a>
                                        <HiChevronDown size={16} style={{marginLeft: 5}}/>
                                    </button>
                                    <div className='header-about-dropdown-menu'>
                                        <Link to="/introduction-text" className='header-about-dropdown-item'>
                                            {t('header.aboutUsSubmenu.introductiontext')}
                                        </Link>
                                        <Link to="/vision" className='header-about-dropdown-item'>
                                            {t('header.aboutUsSubmenu.vision')}
                                        </Link>
                                        <Link to="/mission" className='header-about-dropdown-item'>
                                            {t('header.aboutUsSubmenu.mission')}
                                        </Link>
                                        <Link to="/activities" className='header-about-dropdown-item'>
                                            {t('header.aboutUsSubmenu.activities')}
                                        </Link>
                                        <Link to="/management" className='header-about-dropdown-item'>
                                            {t('header.aboutUsSubmenu.management')}
                                        </Link>
                                        <Link to="/news" className='header-about-dropdown-item'>
                                            {t('header.aboutUsSubmenu.news')}
                                        </Link>
                                    </div>
                                </li>
                            </ul>
                        </div>

                        {/* Mobile Logo */}
                        {/* <div className='d-lg-none header-logo'>
                            <Link to="/">
                                <img src="/assets/images/nordex-logo.png" alt="nordex-logo" width="180px" />
                            </Link>
                        </div> */}

                        {/* Desktop Right Side */}
                        <div className='d-none d-lg-flex align-items-center gap-2 right-navigation-list'>
                            {isAuthenticated ? (
                                <>
                                    {/* Varlık Dropdown */}
                                    <div className='header-dropdown'>
                                        <button
                                            className='header-dropdown-trigger d-flex align-items-center gap-2'
                                            aria-haspopup="true"
                                        >
                                            <SlWallet size={18} />
                                            <span>{t('header.asset')}</span>
                                            <HiChevronDown size={16} />
                                        </button>
                                        <div className='varlik-dropdown-menu'>
                                            {/* Asset Summary Card */}
                                            <div className='asset-summary-card'>
                                                <div className='asset-summary-header'>
                                                    <h3 className='asset-summary-title'>{t('header.assetSummary')}</h3>
                                                    {isBalanceVisible ? (
                                                        <IoEyeOutline
                                                            size={35}
                                                            className='visibility-icon'
                                                            onClick={() => setIsBalanceVisible(false)}
                                                        />
                                                    ) : (
                                                        <IoEyeOffOutline
                                                            size={35}
                                                            className='visibility-icon'
                                                            onClick={() => setIsBalanceVisible(true)}
                                                        />
                                                    )}
                                                </div>
                                                {/* <div className='asset-summary-divider'></div> */}
                                                <div className='asset-balances'>
                                                    <div className='balance-item'>
                                                        <div className='balance-label'>{t('header.availableBalance')}</div>
                                                        <div className='balance-row'>
                                                            <div className='balance-amount'>
                                                                {isBalanceVisible ? (userInfo?.Balance ?? 0.00) : '•••'}
                                                            </div>
                                                            <div className='balance-currency'>USD</div>
                                                            <IoArrowForwardOutline size={16} className='balance-arrow' />
                                                        </div>
                                                    </div>
                                                    <div className='balance-item'>
                                                        <div className='balance-label'>{t('header.availableCredit')}</div>
                                                        <div className='balance-row'>
                                                            <div className='balance-amount'>
                                                                {isBalanceVisible ? (userInfo?.Credit ?? 0.00) : '•••'}
                                                            </div>
                                                            <div className='balance-currency'>USD</div>
                                                            <IoArrowForwardOutline size={16} className='balance-arrow' />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className='asset-actions'>
                                                    <Link to="/deposit" className='w-100'>
                                                        <button className='deposit-btn w-100'>
                                                            {t('header.deposit')}
                                                        </button>
                                                    </Link>
                                                    <Link to="/withdrawal" className='w-100'>
                                                        <button className='withdraw-btn w-100'>
                                                            {t('header.withdraw')}
                                                        </button>
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Navigation Links */}
                                    <div className='header-nav-links'>
                                        <Link to="/documents" className='nav-link'>
                                            <HiOutlineDocumentText size={20} />
                                            <span>{t('header.documents')}</span>
                                        </Link>
                                        <Link to="/market" className='nav-link'>
                                            <SlGraph size={20} />
                                            <span>{t('header.market')}</span>
                                        </Link>
                                        {/* <Link to="/news" className='nav-link'>
                                            <IoNewspaperOutline size={20} />
                                            <span>{t('header.news')}</span>
                                        </Link> */}
                                    </div>

                                    {/* Profil Dropdown */}
                                    <div className='header-dropdown profile-dropdown'>
                                        <button
                                            className='header-dropdown-trigger d-flex align-items-center gap-2'
                                            aria-haspopup="true"
                                        >
                                            <PiUserCircleThin size={30} />
                                        </button>
                                        <div className='profile-dropdown-menu'>
                                            {/* Profile Header */}
                                            <div className='profile-header'>
                                                <div className='profile-avatar'>
                                                    <div className='avatar-circle'>
                                                        <div className='avatar-head'></div>
                                                        <div className='avatar-shoulders'></div>
                                                    </div>
                                                </div>
                                                <div className='profile-info'>
                                                    <div className='username'>{userInfo?.FullName || user?.name || 'User'}</div>
                                                    <div className='badges'>
                                                        {/* <span className='vip-badge'>VIP 0</span> */}
                                                        <span className={`verification-badge${isVerified ? ' verified' : ''}`}>
                                                            <IoShieldOutline size={12} />
                                                            {isVerified ? t('header.verified') : t('header.unverified')}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Menu Items */}
                                            <div className='profile-menu-items'>
                                                <Link to="/account-summary">
                                                    <div className='profile-menu-item'>
                                                        <IoPieChartOutline size={20} />
                                                        <span>{t('header.accountSummary')}</span>
                                                    </div>
                                                </Link>
                                                <Link to="/two-factor-auth">
                                                    <div className='profile-menu-item'>
                                                        <IoShieldOutline size={20} />
                                                        <span>{t('header.twoFactorAuth')}</span>
                                                    </div>
                                                </Link>
                                                <Link to="/referral-code">
                                                    <div className='profile-menu-item'>
                                                        <IoShareSocialOutline size={20} />
                                                        <span>{t('header.referralCode')}</span>
                                                    </div>
                                                </Link>

                                                <Link to="/change-password">
                                                    <div className='profile-menu-item'>
                                                        <IoKeyOutline size={20} />
                                                        <span>{t('header.changePassword')}</span>
                                                    </div>
                                                </Link>

                                                {/* İşlem Geçmişi Accordion */}
                                                <div className="accordion-item">
                                                    <button
                                                        className={`accordion-header ${isTransactionHistoryOpen ? 'open' : ''}`}
                                                        onClick={() => setIsTransactionHistoryOpen(!isTransactionHistoryOpen)}
                                                    >
                                                        <div>
                                                            <IoDocumentTextOutline size={20} style={{ marginRight: '12px' }} />
                                                            <span>{t('header.txHistory')}</span>
                                                        </div>
                                                        {isTransactionHistoryOpen ? <IoChevronUpOutline size={16} /> : <IoChevronDownOutline size={16} />}
                                                    </button>

                                                    {isTransactionHistoryOpen && (
                                                        <div className="accordion-content">
                                                            {transactionHistorySubItems.map((subItem) => (
                                                                <Link
                                                                    key={subItem.id}
                                                                    to={subItem.path}
                                                                    className="sub-menu-item"
                                                                >
                                                                    <span>{subItem.id === 'kripto-cekim' ? t('header.cryptoWithdrawals') : t('header.bankWithdrawals')}</span>
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Pozisyonlar Accordion */}
                                                <div className="accordion-item">
                                                    <button
                                                        className={`accordion-header ${isPositionsOpen ? 'open' : ''}`}
                                                        onClick={() => setIsPositionsOpen(!isPositionsOpen)}
                                                    >
                                                        <div>
                                                            <IoBarChartOutline size={20} style={{ marginRight: '12px' }} />
                                                            <span>{t('header.positions')}</span>
                                                        </div>
                                                        {isPositionsOpen ? <IoChevronUpOutline size={16} /> : <IoChevronDownOutline size={16} />}
                                                    </button>

                                                    {isPositionsOpen && (
                                                        <div className="accordion-content">
                                                            {positionsSubItems.map((subItem) => (
                                                                <Link
                                                                    key={subItem.id}
                                                                    to={subItem.path}
                                                                    className="sub-menu-item"
                                                                >
                                                                    <span>{subItem.id === 'acik-pozisyonlar' ? t('header.openPositions') : t('header.closedPositions')}</span>
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Logout Button */}
                                            <div className='profile-logout-section'>
                                                <button
                                                    className='profile-logout-btn'
                                                    onClick={() => logout()}
                                                >
                                                    <IoLogOutOutline size={20} />
                                                    <span>{t('common.logout')}</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className='header-login-register-buttons d-flex align-items-center'>
                                    <Link to="/login">
                                        <button className='btn-secondary me-3'>
                                            {t('header.login')}
                                        </button>
                                    </Link>
                                    <Link to="/account">
                                        <button className='btn-primary'>{t('header.register')}</button>
                                    </Link>
                                </div>
                            )}
                            <div className='download-dropdown'>
                                <button className='icon-button download-trigger' aria-haspopup="true" aria-expanded="false">
                                    <GoDownload size={22} />
                                </button>
                                <div className='download-menu py-lg-3'>
                                    <div className='download-links-container'>
                                        <a href={appLinks.ios} target="_blank" rel="noreferrer">
                                            <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/3/3c/Download_on_the_App_Store_Badge.svg/1280px-Download_on_the_App_Store_Badge.svg.png" alt="app-store-badge" width="130" />
                                        </a>
                                        <div className='download-separator'></div>
                                        <a href={appLinks.android} target="_blank" rel="noreferrer">
                                            <img src="/assets/images/android-apk.png" alt="android-apk" width="130" />
                                        </a>
                                    </div>
                                    <button className='btn-primary w-100 mt-3'>{t('header.downloadApp')}</button>
                                </div>
                            </div>
                            <button className='icon-button' onClick={toggleTheme} aria-label={t('header.changeTheme')}>
                                {theme === 'light' ? <MdOutlineDarkMode size={22} /> : <PiSunDuotone size={22} />}
                            </button>
                            <button
                                className='icon-button d-flex align-items-center'
                                onClick={() => setLanguage(lang?.startsWith('tr') ? 'en' : 'tr')}
                                aria-label={t('header.changeLanguage')}
                                title={lang?.startsWith('tr') ? 'EN' : 'TR'}
                            >
                                <PiTranslateLight size={22} />
                                <span style={{ fontSize: 14, marginLeft: 6 }}>{lang?.startsWith('tr') ? 'TR' : 'EN'}</span>
                            </button>
                        </div>

                        {/* Mobile Right Side - Visible on tablet/mobile */}
                        <div className='d-lg-none d-flex align-items-center gap-2'>
                            {/* Download Dropdown */}
                            <div className='header-dropdown mobile-visible'>
                                <button
                                    className='icon-button'
                                    aria-haspopup="true"
                                    aria-label={t('header.downloadApp')}
                                >
                                    <IoDownloadOutline size={22} />
                                </button>
                                <div className='download-menu'>
                                    <div className='download-links-container'>
                                        {appLinks.ios && (
                                            <a
                                                href={appLinks.ios}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                className='download-link'
                                            >
                                                <img
                                                    src='https://upload.wikimedia.org/wikipedia/commons/thumb/3/3c/Download_on_the_App_Store_Badge.svg/1280px-Download_on_the_App_Store_Badge.svg.png'
                                                    alt='App Store'
                                                    width={100}
                                                />
                                            </a>
                                        )}
                                        <div className='download-separator'></div>
                                        <a
                                            href='/assets/nordex-v3.apk'
                                            download
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            className='download-link'
                                        >
                                            <img
                                                src='/assets/images/android-apk.png'
                                                alt='Android APK'
                                                width={100}
                                            />
                                        </a>
                                    </div>
                                </div>
                            </div>

                            {/* Theme Toggle */}
                            <button
                                className='icon-button'
                                onClick={toggleTheme}
                                aria-label={t('header.themeToggleAria')}
                            >
                                {theme === 'dark' ? <MdOutlineLightMode size={22} /> : <MdOutlineDarkMode size={22} />}
                            </button>

                            {/* Language Toggle */}
                            <button
                                className='icon-button d-flex align-items-center'
                                onClick={() => setLanguage(lang?.startsWith('tr') ? 'en' : 'tr')}
                                aria-label={t('header.langToggleAria')}
                                title={lang?.startsWith('tr') ? 'EN' : 'TR'}
                            >
                                <PiTranslateLight size={22} />
                                <span style={{ fontSize: 14, marginLeft: 6 }}>{lang?.startsWith('tr') ? 'TR' : 'EN'}</span>
                            </button>
                        </div>

                        {/* Mobile Hamburger Button */}
                        <button
                            className='d-lg-none mobile-menu-toggle'
                            onClick={() => setIsMobileMenuOpen(true)}
                            aria-label="Open menu"
                        >
                            <IoMenuOutline size={28} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Offcanvas Menu */}
            <div className={`mobile-offcanvas${isMobileMenuOpen ? ' show' : ''}`}>
                {/* Backdrop */}
                <div
                    className={`mobile-offcanvas-backdrop${isMobileMenuOpen ? ' show' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                />

                {/* Offcanvas Content */}
                <div className={`mobile-offcanvas-content${isMobileMenuOpen ? ' show' : ''}`}>
                    {/* Header */}
                    <div className='mobile-offcanvas-header'>
                        <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
                            <img src="/assets/images/nordex-logo.png" alt="nordex-logo" width="180px" />
                        </Link>
                        <button
                            className='mobile-menu-close'
                            onClick={() => setIsMobileMenuOpen(false)}
                            aria-label="Close menu"
                        >
                            <IoCloseOutline size={28} />
                        </button>
                    </div>

                    {/* Menu Content */}
                    <div className='mobile-offcanvas-body'>
                        {isAuthenticated ? (
                            <>
                                {/* User Info */}
                                <div className='mobile-user-info'>
                                    <div className='mobile-profile-avatar'>
                                        <div className='avatar-circle'>
                                            <div className='avatar-head'></div>
                                            <div className='avatar-shoulders'></div>
                                        </div>
                                    </div>
                                    <div className='mobile-profile-details'>
                                        <div className='mobile-username'>{userInfo?.FullName || user?.name || 'User'}</div>
                                        <div className='mobile-badges'>
                                            <span className={`verification-badge${isVerified ? ' verified' : ''}`}>
                                                <IoShieldOutline size={12} />
                                                {isVerified ? t('header.verified') : t('header.unverified')}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Asset Summary */}
                                <div className='mobile-asset-summary'>
                                    <div className='mobile-asset-header'>
                                        <h3>{t('header.assetSummary')}</h3>
                                        {isBalanceVisible ? (
                                            <IoEyeOutline
                                                size={20}
                                                onClick={() => setIsBalanceVisible(false)}
                                            />
                                        ) : (
                                            <IoEyeOffOutline
                                                size={20}
                                                onClick={() => setIsBalanceVisible(true)}
                                            />
                                        )}
                                    </div>
                                    <div className='mobile-balances'>
                                        <div className='mobile-balance-item'>
                                            <span>{t('header.availableBalance')}</span>
                                            <strong>{isBalanceVisible ? userInfo?.Balance : '•••'} USD</strong>
                                        </div>
                                        <div className='mobile-balance-item'>
                                            <span>{t('header.availableCredit')}</span>
                                            <strong>{isBalanceVisible ? userInfo?.Credit : '•••'} USD</strong>
                                        </div>
                                    </div>
                                    <div className='mobile-asset-actions'>
                                        <Link to="/deposit" className='w-100' onClick={() => setIsMobileMenuOpen(false)}>
                                            <button className='mobile-deposit-btn'>{t('header.deposit')}</button>
                                        </Link>
                                        <Link to="/withdrawal" className='w-100' onClick={() => setIsMobileMenuOpen(false)}>
                                            <button className='mobile-withdraw-btn'>{t('header.withdraw')}</button>
                                        </Link>
                                    </div>
                                </div>

                                {/* Navigation Links - Group 1: Main Pages */}
                                <div className='mobile-nav-group'>
                                    <div className='mobile-nav-group-header'>{t('header.mainMenu')}</div>
                                    <div className='mobile-nav-group-items'>
                                        {/* About Us Accordion */}
                                        <div className="mobile-accordion-item">
                                            <button
                                                className={`mobile-accordion-header group-primary ${isAboutUsOpen ? 'open' : ''}`}
                                                onClick={() => setIsAboutUsOpen(!isAboutUsOpen)}
                                            >
                                                <div>
                                                    <IoDocumentTextOutline size={20} />
                                                    <span>{t('header.aboutUs')}</span>
                                                </div>
                                                {isAboutUsOpen ? <IoChevronUpOutline size={16} /> : <IoChevronDownOutline size={16} />}
                                            </button>
                                            <div className={`mobile-accordion-content${isAboutUsOpen ? ' show' : ''}`}>
                                                {aboutUsSubItems.map((item) => (
                                                    <Link 
                                                        key={item.id} 
                                                        to={item.path} 
                                                        className="mobile-sub-item" 
                                                        onClick={() => setIsMobileMenuOpen(false)}
                                                    >
                                                        {t(`header.aboutUsSubmenu.${item.id}`)}
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                        {/* Nordex Akademi Accordion */}
                                        <div className="mobile-accordion-item">
                                            <button
                                                className={`mobile-accordion-header group-primary ${isAcademyOpen ? 'open' : ''}`}
                                                onClick={() => setIsAcademyOpen(!isAcademyOpen)}
                                            >
                                                <div>
                                                    <IoBarChartOutline size={20} />
                                                    <span>{t('header.nordexAcademy')}</span>
                                                </div>
                                                {isAcademyOpen ? <IoChevronUpOutline size={16} /> : <IoChevronDownOutline size={16} />}
                                            </button>
                                            <div className={`mobile-accordion-content${isAcademyOpen ? ' show' : ''}`}>
                                                {academySubItems.map((item) => (
                                                    <Link 
                                                        key={item.id} 
                                                        to={item.path} 
                                                        className="mobile-sub-item" 
                                                        onClick={() => setIsMobileMenuOpen(false)}
                                                    >
                                                        {t(`header.academySubmenu.${item.id}`)}
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>

                                        <Link to="/investment-options" className='mobile-nav-item group-primary' onClick={() => setIsMobileMenuOpen(false)}>
                                            <IoAnalyticsOutline size={20} />
                                            <span>{t('header.investmentOptions')}</span>
                                        </Link>
                                        <Link to="/promotions" className='mobile-nav-item group-primary' onClick={() => setIsMobileMenuOpen(false)}>
                                            <IoMegaphoneOutline size={20} />
                                            <span>{t('header.promotions')}</span>
                                        </Link>
                                    </div>
                                </div>

                                {/* Navigation Links - Group 2: Trading & Documents */}
                                <div className='mobile-nav-group'>
                                    <div className='mobile-nav-group-header'>{t('header.tradingMenu')}</div>
                                    <div className='mobile-nav-group-items'>
                                        <Link to="/market" className='mobile-nav-item group-secondary' onClick={() => setIsMobileMenuOpen(false)}>
                                            <IoTrendingUpOutline size={20} />
                                            <span>{t('header.market')}</span>
                                        </Link>
                                        <Link to="/documents" className='mobile-nav-item group-secondary' onClick={() => setIsMobileMenuOpen(false)}>
                                            <IoDocumentTextOutline size={20} />
                                            <span>{t('header.documents')}</span>
                                        </Link>
                                    </div>
                                </div>

                                {/* Navigation Links - Group 3: Account Settings */}
                                <div className='mobile-nav-group'>
                                    <div className='mobile-nav-group-header'>{t('header.accountMenu')}</div>
                                    <div className='mobile-nav-group-items'>
                                        <Link to="/account-summary" className='mobile-nav-item group-tertiary' onClick={() => setIsMobileMenuOpen(false)}>
                                            <IoPieChartOutline size={20} />
                                            <span>{t('header.accountSummary')}</span>
                                        </Link>
                                        <Link to="/two-factor-auth" className='mobile-nav-item group-tertiary' onClick={() => setIsMobileMenuOpen(false)}>
                                            <IoShieldOutline size={20} />
                                            <span>{t('header.twoFactorAuth')}</span>
                                        </Link>
                                        <Link to="/referral-code" className='mobile-nav-item group-tertiary' onClick={() => setIsMobileMenuOpen(false)}>
                                            <IoShareSocialOutline size={20} />
                                            <span>{t('header.referralCode')}</span>
                                        </Link>
                                        <Link to="/change-password" className='mobile-nav-item group-tertiary' onClick={() => setIsMobileMenuOpen(false)}>
                                            <IoKeyOutline size={20} />
                                            <span>{t('header.changePassword')}</span>
                                        </Link>

                                        {/* Transaction History */}
                                        <div className="mobile-accordion-item">
                                            <button
                                                className={`mobile-accordion-header group-tertiary ${isTransactionHistoryOpen ? 'open' : ''}`}
                                                onClick={() => setIsTransactionHistoryOpen(!isTransactionHistoryOpen)}
                                            >
                                                <div>
                                                    <IoDocumentTextOutline size={20} />
                                                    <span>{t('header.txHistory')}</span>
                                                </div>
                                                {isTransactionHistoryOpen ? <IoChevronUpOutline size={16} /> : <IoChevronDownOutline size={16} />}
                                            </button>
                                            <div className={`mobile-accordion-content${isTransactionHistoryOpen ? ' show' : ''}`}>
                                                <Link to="/crypto-withdrawals" className="mobile-sub-item" onClick={() => setIsMobileMenuOpen(false)}>
                                                    {t('header.cryptoWithdrawals')}
                                                </Link>
                                                <Link to="/bank-withdrawals" className="mobile-sub-item" onClick={() => setIsMobileMenuOpen(false)}>
                                                    {t('header.bankWithdrawals')}
                                                </Link>
                                            </div>
                                        </div>

                                        {/* Positions */}
                                        <div className="mobile-accordion-item">
                                            <button
                                                className={`mobile-accordion-header group-tertiary ${isPositionsOpen ? 'open' : ''}`}
                                                onClick={() => setIsPositionsOpen(!isPositionsOpen)}
                                        >
                                            <div>
                                                <IoBarChartOutline size={20} />
                                                <span>{t('header.positions')}</span>
                                            </div>
                                            {isPositionsOpen ? <IoChevronUpOutline size={16} /> : <IoChevronDownOutline size={16} />}
                                        </button>
                                        <div className={`mobile-accordion-content${isPositionsOpen ? ' show' : ''}`}>
                                            <Link to="/open-positions" className="mobile-sub-item" onClick={() => setIsMobileMenuOpen(false)}>
                                                {t('header.openPositions')}
                                            </Link>
                                            <Link to="/closed-positions" className="mobile-sub-item" onClick={() => setIsMobileMenuOpen(false)}>
                                                {t('header.closedPositions')}
                                            </Link>
                                        </div>
                                    </div>
                                    </div>
                                </div>

                                {/* Logout Button */}
                                <div className='mobile-logout-section'>
                                    <button
                                        className='mobile-logout-btn'
                                        onClick={() => {
                                            logout();
                                            setIsMobileMenuOpen(false);
                                        }}
                                    >
                                        <IoLogOutOutline size={20} />
                                        <span>{t('common.logout')}</span>
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Guest Navigation */}
                                <div className='mobile-nav-group'>
                                    <div className='mobile-nav-group-header'>{t('header.mainMenu')}</div>
                                    <div className='mobile-nav-group-items'>
                                        <Link to="/login" className='mobile-nav-item group-primary' onClick={() => setIsMobileMenuOpen(false)}>
                                            <IoTrendingUpOutline size={20} />
                                            <span>{t('header.buyCrypto')}</span>
                                        </Link>

                                        {/* About Us Accordion */}
                                        <div className="mobile-accordion-item">
                                            <button
                                                className={`mobile-accordion-header group-primary ${isAboutUsOpen ? 'open' : ''}`}
                                                onClick={() => setIsAboutUsOpen(!isAboutUsOpen)}
                                            >
                                                <div>
                                                    <IoDocumentTextOutline size={20} />
                                                    <span>{t('header.aboutUs')}</span>
                                                </div>
                                                {isAboutUsOpen ? <IoChevronUpOutline size={16} /> : <IoChevronDownOutline size={16} />}
                                            </button>
                                            <div className={`mobile-accordion-content${isAboutUsOpen ? ' show' : ''}`}>
                                                {aboutUsSubItems.map((item) => (
                                                    <Link 
                                                        key={item.id} 
                                                        to={item.path} 
                                                        className="mobile-sub-item" 
                                                        onClick={() => setIsMobileMenuOpen(false)}
                                                    >
                                                        {t(`header.aboutUsSubmenu.${item.id}`)}
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Nordex Akademi Accordion */}
                                        <div className="mobile-accordion-item">
                                            <button
                                                className={`mobile-accordion-header group-primary ${isAcademyOpen ? 'open' : ''}`}
                                                onClick={() => setIsAcademyOpen(!isAcademyOpen)}
                                            >
                                                <div>
                                                    <IoBarChartOutline size={20} />
                                                    <span>{t('header.nordexAcademy')}</span>
                                                </div>
                                                {isAcademyOpen ? <IoChevronUpOutline size={16} /> : <IoChevronDownOutline size={16} />}
                                            </button>
                                            <div className={`mobile-accordion-content${isAcademyOpen ? ' show' : ''}`}>
                                                {academySubItems.map((item) => (
                                                    <Link 
                                                        key={item.id} 
                                                        to={item.path} 
                                                        className="mobile-sub-item" 
                                                        onClick={() => setIsMobileMenuOpen(false)}
                                                    >
                                                        {t(`header.academySubmenu.${item.id}`)}
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>

                                        <Link to="/news" className='mobile-nav-item group-primary' onClick={() => setIsMobileMenuOpen(false)}>
                                            <IoNewspaperOutline size={20} />
                                            <span>{t('header.aboutUsSubmenu.news')}</span>
                                        </Link>
                                        <Link to="/investment-options" className='mobile-nav-item group-primary' onClick={() => setIsMobileMenuOpen(false)}>
                                            <IoAnalyticsOutline size={20} />
                                            <span>{t('header.investmentOptions')}</span>
                                        </Link>
                                        <Link to="/promotions" className='mobile-nav-item group-primary' onClick={() => setIsMobileMenuOpen(false)}>
                                            <IoMegaphoneOutline size={20} />
                                            <span>{t('header.promotions')}</span>
                                        </Link>
                                    </div>
                                </div>

                                {/* Login/Register Buttons */}
                                <div className='mobile-auth-buttons'>
                                    <Link to="/login" className='w-100' onClick={() => setIsMobileMenuOpen(false)}>
                                        <button className='btn-secondary w-100 mb-3'>
                                            {t('header.login')}
                                        </button>
                                    </Link>
                                    <Link to="/account" className='w-100' onClick={() => setIsMobileMenuOpen(false)}>
                                        <button className='btn-primary w-100'>
                                            {t('header.register')}
                                        </button>
                                    </Link>
                                </div>
                            </>
                        )}

                        {/* Settings */}
                        {/* <div className='mobile-settings'>
                            <button className='mobile-setting-item' onClick={toggleTheme}>
                                {theme === 'light' ? <MdDarkMode size={20} /> : <MdOutlineLightMode size={20} />}
                                <span>{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>
                            </button>
                            <button
                                className='mobile-setting-item'
                                onClick={() => setLanguage(lang?.startsWith('tr') ? 'en' : 'tr')}
                            >
                                <IoLanguageOutline size={20} />
                                <span>{lang?.startsWith('tr') ? 'English' : 'Türkçe'}</span>
                            </button>
                        </div> */}

                        {/* Mobile App Download Section */}
                        {/* <div className='mobile-app-download'>
                            <div className='mobile-setting-item'>
                                <IoDownloadOutline size={20} />
                                <span>{t('header.downloadApp')}</span>
                            </div>
                            <div className='mobile-download-buttons py-3 px-3 d-flex flex-column gap-3'>
                                <a href={appLinks.ios} target="_blank" rel="noreferrer" className='mobile-download-btn'>
                                    <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/3/3c/Download_on_the_App_Store_Badge.svg/1280px-Download_on_the_App_Store_Badge.svg.png" alt="app-store-badge" width="135" />
                                </a>
                                <a href={appLinks.android} target="_blank" rel="noreferrer" className='mobile-download-btn'>
                                    <img src="/assets/images/android-apk.png" alt="android-apk" width="135" />
                                </a>
                            </div>
                        </div> */}
                    </div>
                </div>
            </div>
        </>
    );
}